import {
  gameEventNames,
  onGameEvent,
  type GameStateDetail,
} from '../game/events';
import { formatElapsedTime } from '../game/result';
import { resolveQuerySelector, setText } from './dom';

export interface HeaderDeps {
  readonly initialLevelNumber: number;
  readonly clamp: (value: number, min: number, max: number) => number;
  readonly difficultyLabelForLevel: (levelNumber: number) => string;
  readonly intensityForLevel: (levelNumber: number) => string;
  readonly onHintAvailable: (available: boolean) => void;
  readonly onCompleteChanged: (complete: boolean) => void;
}

export interface HeaderApi {
  setMessage(text: string, tone: GameStateDetail['tone']): void;
  destroy(): void;
}

export function createHeader(deps: HeaderDeps): HeaderApi {
  const querySelector = resolveQuerySelector();
  const remainingCount = querySelector<HTMLElement>('#remaining-count');
  const timeReadout = querySelector<HTMLElement>('#time-readout');
  const totalCount = querySelector<HTMLElement>('#total-count');
  const failIndicator = querySelector<HTMLElement>('#fail-indicator');
  const failSlots = document.querySelectorAll<HTMLElement>('.fail-indicator__slot');
  const headerLevel = querySelector<HTMLElement>('#header-level');
  const headerDifficulty = querySelector<HTMLElement>('#header-difficulty');
  const message = querySelector<HTMLElement>('#game-message');
  const appShell = querySelector<HTMLElement>('.app-shell');
  let timeReadoutFrozen = false;

  const setMessage = (text: string, tone: GameStateDetail['tone']): void => {
    if (message !== null) {
      message.textContent = text;
      message.dataset.tone = tone;
    }
  };

  const renderFailIndicator = (failCount: number, maxFailures: number): void => {
    const maximum = Math.max(0, maxFailures);
    const count = deps.clamp(failCount, 0, maximum);
    failIndicator?.setAttribute('aria-label', `失敗 ${count} / ${maximum}`);
    failSlots.forEach((slot, index) => {
      slot.classList.toggle('fail-indicator__slot--active', index < count);
      slot.hidden = index >= maximum;
    });
  };

  const renderHeader = (levelNumber: number): void => {
    setText(headerLevel, `LEVEL ${levelNumber}`);
    setText(headerDifficulty, deps.difficultyLabelForLevel(levelNumber));
    // シェルの色調（難易度プレート・結果画面の見出し）を難易度色へ切り替える。
    appShell?.setAttribute('data-difficulty', deps.intensityForLevel(levelNumber));
  };

  renderHeader(deps.initialLevelNumber);

  const disposers = [
    onGameEvent(gameEventNames.state, (detail) => {
      setText(remainingCount, String(detail.remaining));
      setText(totalCount, String(detail.total));
      renderFailIndicator(detail.failCount, detail.maxFailures);
      deps.onHintAvailable(detail.hintAvailable);
      setMessage(detail.message, detail.tone);
      deps.onCompleteChanged(detail.complete);
    }),
    onGameEvent(gameEventNames.setLevel, (detail) => {
      renderHeader(detail.levelNumber);
      timeReadoutFrozen = false;
      setText(timeReadout, '00:00');
    }),
    onGameEvent(gameEventNames.timeTick, (detail) => {
      if (timeReadoutFrozen) {
        return;
      }
      setText(timeReadout, formatElapsedTime(detail.elapsedMs));
    }),
    onGameEvent(gameEventNames.levelClear, (detail) => {
      timeReadoutFrozen = true;
      setText(timeReadout, formatElapsedTime(detail.elapsedMs));
    }),
  ];

  return {
    setMessage,
    destroy: () => disposers.forEach((dispose) => dispose()),
  };
}
